'use client'

import { Divider } from '@macarongfactory/web-ui'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { useContentNavigation } from '@/shared/model'

import CarStoryContentDetailAppDownloadSection from './CarStoryContentDetailAppDownloadSection'
import CarStoryContentDetailCta from './CarStoryContentDetailCta'
import CarStoryContentDetailExternalTopNavigation from './CarStoryContentDetailExternalTopNavigation'
import CarStoryContentDetailShareBottomSheet from './CarStoryContentDetailShareBottomSheet'
import CarStoryContentDetailShareSection from './CarStoryContentDetailShareSection'
import CarStoryContentDetailTopNavigation from './CarStoryContentDetailTopNavigation'
import CarStoryTabLoading from './CarStoryTabLoading'
import useMycleContentDetailQuery from '../api/query/useMycleContentDetailQuery'
import {
  CAR_STORY_KAKAO_TEMPLATE_ID,
  getCarStoryShareUrl,
} from '../lib/shareMeta'
import { carStoryTracking } from '../lib/tracking'
import useCarStoryShare, {
  type ICarStoryShareMeta,
} from '../model/useCarStoryShare'

import type { IMycleContentDetailResponse } from '../model/home'

interface ICarStoryContentDetailPageProps {
  contentId: number
  isInApp: boolean
}

const buildShareMeta = (
  url: string,
  detail: IMycleContentDetailResponse
): ICarStoryShareMeta => ({
  url,
  title: detail.kakaoShareTitle ?? '',
  description: detail.kakaoShareDescription ?? '',
  imageUrl: detail.kakaoShareThumbUrl ?? '',
  kakaoTemplateId: CAR_STORY_KAKAO_TEMPLATE_ID,
})

/** detail 로드 전 useCarStoryShare 호출 시 사용할 placeholder. Hook은 조건부 호출 불가하기 때문에 필요 */
const EMPTY_SHARE_META: ICarStoryShareMeta = {
  url: '',
  title: '',
  description: '',
  imageUrl: '',
  kakaoTemplateId: CAR_STORY_KAKAO_TEMPLATE_ID,
}

const CarStoryContentDetailPage = ({
  contentId,
  isInApp,
}: ICarStoryContentDetailPageProps) => {
  const contentRef = useContentNavigation()
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false)

  const { data: detail, isLoading } = useMycleContentDetailQuery(contentId)

  const hasTrackedRef = useRef(false)
  useEffect(
    function trackDetailPageView() {
      if (hasTrackedRef.current || !detail) return
      hasTrackedRef.current = true
      carStoryTracking.showDetailPage(detail.title, contentId)
    },
    [detail, contentId]
  )

  const shareMeta = detail
    ? buildShareMeta(getCarStoryShareUrl(contentId), detail)
    : null

  const { handleKakaoShare, handleCopyLink, handleShareMore } =
    useCarStoryShare({ shareMeta: shareMeta ?? EMPTY_SHARE_META })

  const openShareSheet = () => setIsShareSheetOpen(true)
  const closeShareSheet = useCallback(() => setIsShareSheetOpen(false), [])

  const withCloseSheet = useCallback(
    (handler: () => void | Promise<void>) => async () => {
      await handler()
      closeShareSheet()
    },
    [closeShareSheet]
  )

  const topNavigation: ReactNode = isInApp ? (
    <CarStoryContentDetailTopNavigation
      contentId={contentId}
      onShareClick={openShareSheet}
    />
  ) : (
    <CarStoryContentDetailExternalTopNavigation contentId={contentId} />
  )

  if (isLoading || !detail || !shareMeta) {
    return (
      <>
        {topNavigation}
        <CarStoryTabLoading />
      </>
    )
  }

  return (
    <div className='pb-dimension-x28'>
      {topNavigation}
      <main ref={contentRef} role='main' aria-label='차량 이야기 콘텐츠 상세'>
        <article
          className='car-story-content'
          // 서버(getMycleContentDetailServer, /api/car-story/contents/[id])에서 정제된 HTML
          dangerouslySetInnerHTML={{ __html: detail.contentHtml }}
        />
      </main>
      <div className='px-dimension-x5'>
        <Divider.Horizontal
          strokeWidth='stroke-width-x1'
          color='stroke-neutral-strong'
        />
      </div>
      {isInApp ? (
        <>
          <CarStoryContentDetailShareSection
            contentId={contentId}
            shareMeta={shareMeta}
          />
          {detail.ctaLabel && detail.ctaUrl && (
            <CarStoryContentDetailCta
              contentId={contentId}
              title={detail.title}
              label={detail.ctaLabel}
              url={detail.ctaUrl}
              initialLikeCount={detail.likeCount}
              initialLiked={detail.liked}
            />
          )}
        </>
      ) : (
        <CarStoryContentDetailAppDownloadSection contentId={contentId} />
      )}
      <CarStoryContentDetailShareBottomSheet
        contentId={contentId}
        isOpen={isShareSheetOpen}
        onClose={closeShareSheet}
        onShareKakao={withCloseSheet(handleKakaoShare)}
        onCopyUrl={withCloseSheet(handleCopyLink)}
        onShareMore={withCloseSheet(handleShareMore)}
      />
    </div>
  )
}

export default CarStoryContentDetailPage
